package org.teddapp.project.core

import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import org.teddapp.project.db.nowMillis
import org.teddapp.project.db.randomUuid

/**
 * Session du noyau au niveau de l'application : ouverte au démarrage, elle rattrape
 * l'instantané puis relit le fil des réactions. L'interface ne calcule rien, elle lit.
 *
 * Quand la bibliothèque native manque, [bridge] vaut null. Si un [remote] est branché
 * (web, iOS connectés au serveur), les faits partent au serveur et l'instantané en
 * revient : fiches, heures et paliers sont les mêmes que sur les appareils à noyau.
 * Sans l'un ni l'autre, tout continue en local : la recommandation retombe sur la
 * première tâche du pool, le fil reste vide.
 */
class CoreSession(
    val bridge: TeddCoreBridge?,
    private val scope: CoroutineScope,
    private val deviceId: String = "inconnu",
) {

    private val _reactions = MutableStateFlow<List<CoreReaction>>(emptyList())

    /** Fil des réactions non lues, de la plus récente à la plus ancienne. */
    val reactions: StateFlow<List<CoreReaction>> = _reactions

    private val _snapshot = MutableStateFlow<CoreSnapshot?>(null)

    /** Instantané de projection : heures par compétence, finances, cohérence. */
    val snapshot: StateFlow<CoreSnapshot?> = _snapshot

    private val _recommendation = MutableStateFlow<CoreRecommendation?>(null)

    /** Ce que l'agent propose maintenant, ou null sans noyau. */
    val recommendation: StateFlow<CoreRecommendation?> = _recommendation

    private var cursor: Long = 0

    private val _lastSync = MutableStateFlow<CoreSyncReport?>(null)

    /** Bilan du dernier cycle de synchronisation du journal, ou null si aucun. */
    val lastSync: StateFlow<CoreSyncReport?> = _lastSync

    /** Transport à utiliser pour le journal ; null tant que la sauvegarde n'est pas configurée. */
    var transport: CoreJournalTransport? = null

    /** Le noyau distant, pour un appareil sans bibliothèque native ; null sinon. */
    var remote: RemoteCore? = null

    /** Vrai si les faits ont quelque part où aller : un noyau sur place, ou le serveur. */
    val available: Boolean get() = bridge != null || remote != null

    /** Vrai si le noyau est sur place : réactions, recommandation et lecture immédiate. */
    val local: Boolean get() = bridge != null

    /** Offset local des faits écrits de loin : cet appareil n'a pas de journal, il compte. */
    private var remoteOffset: Long = 0

    /**
     * Contexte de la dernière demande de recommandation.
     *
     * On le garde pour pouvoir en redemander une après chaque fait : l'agent ne note
     * que la recommandation qu'il vient de rendre, donc sans la rafraîchir il
     * n'apprendrait qu'une seule fois par lancement de l'application.
     */
    private data class RecoContext(
        val fatigue: String,
        val focusStreak: Int,
        val physicalReady: Boolean,
    )

    private var context: RecoContext? = null

    /** Rattrape l'instantané et charge le fil. Appelé une fois au démarrage. */
    fun start(timeOfDay: String, fatigue: String, focusStreak: Int, physicalReady: Boolean) {
        val core = bridge ?: return
        context = RecoContext(fatigue, focusStreak, physicalReady)
        scope.launch(Dispatchers.Default) {
            runCatching {
                // D'abord les faits venus des autres appareils, puis la projection. Le
                // transport n'est peut-être pas encore branché (la synchro se monte
                // après) : alors rien ne part ici, et le battement suivant rattrape.
                syncJournal()
                core.catchUp()
                val fresh = core.reactionsSince(cursor)
                cursor = fresh.cursor
                _reactions.value = fresh.reactions.reversed()
                _snapshot.value = parseCoreSnapshot(core.snapshotJson())
                _recommendation.value = core.recommendation(timeOfDay, fatigue, focusStreak, physicalReady)
            }.onFailure { println("[noyau] démarrage : ${it.message}") }
        }
    }

    /** Écrit un fait, puis rafraîchit le fil : une réaction peut en découler. */
    fun record(userId: String, eventJson: String) {
        val core = bridge ?: return recordRemote(userId, listOf(eventJson))
        scope.launch(Dispatchers.Default) {
            runCatching {
                core.appendEvent(userId, eventJson)
                core.catchUp()
                val fresh = core.reactionsSince(cursor)
                cursor = fresh.cursor
                if (fresh.reactions.isNotEmpty()) {
                    _reactions.value = fresh.reactions.reversed() + _reactions.value
                }
                _snapshot.value = parseCoreSnapshot(core.snapshotJson())
                // Le fait vient de noter la recommandation précédente ; on en redemande
                // une, sinon l'agent n'aurait plus rien à noter jusqu'au prochain lancement.
                refreshRecommendation()
            }.onFailure { println("[noyau] écriture : ${it.message}") }
            // Les autres appareils l'attendent : il part tout de suite, sans attendre le battement.
            syncJournal()
        }
    }

    /**
     * Écrit une suite de faits dans l'ordre donné, puis ne relit l'instantané qu'une fois.
     *
     * Un parcours redéclaré vient avec ses feuilles déjà cochées : les écrire une à une
     * avec [record] les lancerait en parallèle, sans garantie que la déclaration passe
     * d'abord, et relirait l'instantané autant de fois.
     */
    fun recordAll(userId: String, events: List<String>) {
        if (events.isEmpty()) return
        val core = bridge ?: return recordRemote(userId, events)
        scope.launch(Dispatchers.Default) {
            runCatching {
                events.forEach { core.appendEvent(userId, it) }
                core.catchUp()
                val fresh = core.reactionsSince(cursor)
                cursor = fresh.cursor
                if (fresh.reactions.isNotEmpty()) {
                    _reactions.value = fresh.reactions.reversed() + _reactions.value
                }
                _snapshot.value = parseCoreSnapshot(core.snapshotJson())
                refreshRecommendation()
            }.onFailure { println("[noyau] écriture groupée : ${it.message}") }
            syncJournal()
        }
    }

    /**
     * Écrit un fait et rend l'instantané qui en découle, sans attendre.
     *
     * La version qui ne rend rien suffit quand l'écran n'a besoin de rien en retour.
     * Une révision, elle, veut la case et l'échéance immédiatement : c'est le noyau
     * qui les décide, donc il faut les lui demander dans la foulée.
     */
    suspend fun recordAndRead(userId: String, eventJson: String): CoreSnapshot? {
        val core = bridge ?: return null
        return runCatching {
            core.appendEvent(userId, eventJson)
            core.catchUp()
            val fresh = core.reactionsSince(cursor)
            cursor = fresh.cursor
            if (fresh.reactions.isNotEmpty()) {
                _reactions.value = fresh.reactions.reversed() + _reactions.value
            }
            val snapshot = parseCoreSnapshot(core.snapshotJson())
            _snapshot.value = snapshot
            refreshRecommendation()
            snapshot
        }.onFailure { println("[noyau] écriture : ${it.message}") }.getOrNull()
    }

    /**
     * Sans noyau sur place : les faits partent au serveur dans leur enveloppe, puis
     * l'instantané en revient. Sans serveur non plus, rien ne se passe.
     */
    private fun recordRemote(userId: String, events: List<String>) {
        val wire = remote ?: return
        scope.launch(Dispatchers.Default) {
            runCatching {
                // Les enveloppes se numérotent sous le verrou : deux écritures lancées
                // ensemble ne se partagent pas un offset.
                remoteLock.withLock { wire.publish(events.map { envelope(userId, it) }) }
                _snapshot.value = parseCoreSnapshot(remoteLock.withLock { wire.snapshotJson() })
            }.onFailure { println("[noyau] écriture distante : ${it.message}") }
        }
    }

    /** L'enveloppe d'un fait, telle que le noyau l'écrirait : identifiant, offset, appareil, heure, auteur. */
    internal fun envelope(userId: String, eventJson: String): String {
        remoteOffset += 1
        return "{\"event_id\":\"${randomUuid()}\",\"offset\":$remoteOffset,\"device_id\":\"$deviceId\"," +
            "\"timestamp_ms\":${nowMillis()},\"user_id\":\"$userId\",\"payload\":$eventJson}"
    }

    /** Un appel distant à la fois : l'instantané se lit après la publication, pas pendant. */
    private val remoteLock = Mutex()

    /** Ferme une réaction : elle disparaît du fil et ne revient pas. */
    fun dismiss(reaction: CoreReaction) {
        _reactions.value = _reactions.value.filterNot { it.offset == reaction.offset && it.kind == reaction.kind }
    }

    /** Nombre de faits dans le journal local, répliques comprises. */
    fun eventCount(): Long = bridge?.eventCount() ?: 0L

    /**
     * Lance un cycle de synchronisation du journal et rejoue ce qui en revient.
     *
     * Sans transport configuré, ou hors ligne, la fonction ne fait rien et le
     * curseur n'avance pas : le cycle suivant reprendra exactement où il en était.
     */
    fun sync() {
        scope.launch(Dispatchers.Default) { syncNow() }
    }

    /** Même cycle, attendable : le moteur de sync l'enchaîne avec le reste des tables. */
    suspend fun syncNow(): CoreSyncReport? {
        syncJournal()
        bridge?.let { core ->
            runCatching {
                core.catchUp()
                _snapshot.value = parseCoreSnapshot(core.snapshotJson())
            }
        }
        if (bridge == null) remote?.let { wire ->
            runCatching { remoteLock.withLock { wire.snapshotJson() } }
                .onSuccess { _snapshot.value = parseCoreSnapshot(it) }
                .onFailure { println("[noyau] instantané distant : ${it.message}") }
        }
        return _lastSync.value
    }

    /** Redemande une recommandation dans le contexte courant, en réarmant l'apprentissage. */
    private fun refreshRecommendation() {
        val core = bridge ?: return
        val ctx = context ?: return
        runCatching {
            _recommendation.value = core.recommendation(
                timeOfDay = currentTimeOfDay(nowMillis()),
                fatigue = ctx.fatigue,
                focusStreak = ctx.focusStreak,
                physicalReady = ctx.physicalReady,
            )
        }
    }

    /** Un cycle à la fois : le démarrage et le battement de la sync peuvent se croiser. */
    private val journalLock = Mutex()

    private suspend fun syncJournal() {
        val core = bridge ?: return
        val wire = transport ?: return
        journalLock.withLock {
            runCatching {
                withContext(Dispatchers.Default) { wire.prepare(core) }
                core.synchronize(wire).also { wire.afterCycle() }
            }
                .onSuccess { _lastSync.value = it }
                .onFailure { println("[noyau] synchronisation : ${it.message}") }
        }
    }
}

/** Session du noyau accessible depuis l'arbre Compose ; null tant qu'elle n'est pas fournie. */
val LocalCoreSession = staticCompositionLocalOf<CoreSession?> { null }

/** Tranche horaire attendue par l'agent, déduite de l'horloge. */
fun currentTimeOfDay(nowMillis: Long): String {
    val hour = ((nowMillis / 3_600_000L) % 24L).toInt()
    return when (hour) {
        in 6..11 -> "morning"
        in 12..17 -> "afternoon"
        in 18..23 -> "evening"
        else -> "night"
    }
}

/**
 * Lit un champ de premier niveau dans une enveloppe JSON, chaîne ou nombre.
 * On évite d'ajouter un analyseur complet pour trois champs stables et connus.
 */
internal fun jsonField(json: String, name: String): String? {
    val key = "\"" + name + "\":"
    val start = json.indexOf(key)
    if (start < 0) return null
    var i = start + key.length
    while (i < json.length && json[i] == ' ') i++
    if (i >= json.length) return null
    return if (json[i] == '"') {
        val end = json.indexOf('"', i + 1)
        if (end < 0) null else json.substring(i + 1, end)
    } else {
        val end = generateSequence(i) { it + 1 }
            .firstOrNull { it >= json.length || json[it] == ',' || json[it] == '}' } ?: json.length
        json.substring(i, end).trim().takeIf { it.isNotEmpty() }
    }
}
